import React from 'react';
import { Link } from 'react-router-dom';
import { Monitor, Cpu, HardDrive, MemoryStick, CheckCircle, Truck, Clock, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const HardwareSpecials = () => {
  const products = [
    {
      id: 1,
      name: 'Lenovo ThinkCentre M70 Tiny + 23.8" Screen',
      price: 1299,
      badge: 'March Special',
      image: '/images/products/lenovo-m70-gen5.jpg',
      description: 'Powerful compact desktop solution perfect for business and home office use.',
      specs: [
        { icon: Cpu, label: 'CPU', value: '14th Gen Intel® Core™ i5-14400' },
        { icon: Monitor, label: 'OS', value: 'Windows 11 Pro 64' },
        { icon: MemoryStick, label: 'Memory', value: '16 GB DDR5-5600MT/s' },
        { icon: HardDrive, label: 'Storage', value: '512 GB SSD M.2 2280' }
      ],
      includes: [
        'Lenovo ThinkCentre M70 Tiny Desktop',
        '23.8" LED Monitor',
        'Keyboard',
        'Mouse',
        'All cables and accessories',
        '3 Year Warranty'
      ],
      screenUpgrade: {
        available: true,
        size: '27"',
        price: 49
      },
      delivery: {
        available: true,
        price: 149,
        description: 'Delivery + Setup (1 hour onsite)'
      },
      stock: {
        status: 'limited',
        message: 'Brand new units - flying off the shelves!'
      }
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block mb-6">
            <span className="px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-bold">
              LIMITED TIME OFFERS
            </span>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Hardware Specials</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Premium business hardware at exceptional prices. All systems come with warranty and professional setup available.
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          {products.map((product) => (
            <Card key={product.id} className="overflow-hidden shadow-2xl hover:shadow-3xl transition-shadow duration-300 mb-12">
              <CardContent className="p-0">
                <div className="grid lg:grid-cols-2 gap-0">
                  {/* Product Image */}
                  <div className="relative bg-gray-100 flex items-center justify-center p-12">
                    <div className="absolute top-6 right-6">
                      <span className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold shadow-lg flex items-center space-x-2">
                        <Tag size={16} />
                        <span>{product.badge}</span>
                      </span>
                    </div>
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="max-w-full h-auto object-contain rounded-lg"
                      style={{ maxHeight: '500px' }}
                    />
                  </div>

                  {/* Product Details */}
                  <div className="p-8 lg:p-12 flex flex-col">
                    <div className="flex-1">
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">{product.name}</h2>
                      <p className="text-gray-600 mb-6">{product.description}</p>

                      {/* Price */}
                      <div className="mb-8 p-6 bg-blue-50 rounded-xl border-2 border-blue-200">
                        <div className="flex items-baseline space-x-2 mb-2">
                          <span className="text-5xl font-bold text-blue-600">${product.price}</span>
                          <span className="text-gray-600">inc. GST</span>
                        </div>
                        {product.stock && (
                          <div className="mt-4 flex items-center space-x-2 text-red-600">
                            <span className="relative flex h-3 w-3">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                            </span>
                            <span className="text-sm font-bold">{product.stock.message}</span>
                          </div>
                        )}
                      </div>

                      {/* Technical Specs */}
                      <div className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Technical Specifications</h3>
                        <div className="space-y-4">
                          {product.specs.map((spec, index) => (
                            <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <spec.icon className="text-blue-600" size={20} />
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-gray-700">{spec.label}</p>
                                <p className="text-gray-900">{spec.value}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* What's Included */}
                      <div className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">What's Included</h3>
                        <div className="space-y-2">
                          {product.includes.map((item, index) => (
                            <div key={index} className="flex items-center space-x-3">
                              <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
                              <span className="text-gray-700">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Screen Upgrade Option */}
                      {product.screenUpgrade && product.screenUpgrade.available && (
                        <div className="mb-8 p-6 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                          <div className="flex items-start space-x-4">
                            <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Monitor className="text-white" size={20} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900 mb-1">Screen Upgrade Available</h4>
                              <p className="text-gray-700 mb-2">
                                Upgrade to {product.screenUpgrade.size} monitor for enhanced viewing experience
                              </p>
                              <p className="text-2xl font-bold text-amber-700">+${product.screenUpgrade.price}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Delivery Option */}
                      {product.delivery.available && (
                        <div className="mb-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                          <div className="flex items-start space-x-4">
                            <div className="flex space-x-2">
                              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Truck className="text-white" size={20} />
                              </div>
                              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Clock className="text-white" size={20} />
                              </div>
                            </div>
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-900 mb-1">Optional Add-on</h4>
                              <p className="text-gray-700 mb-2">{product.delivery.description}</p>
                              <p className="text-2xl font-bold text-green-700">${product.delivery.price}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* CTA Buttons */}
                    <div className="space-y-3 pt-6 border-t border-gray-200">
                      <Link to="/contact" className="block">
                        <Button size="lg" className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 transition-all duration-200">
                          Order Now
                        </Button>
                      </Link>
                      <Link to="/contact" className="block">
                        <Button size="lg" variant="outline" className="w-full border-gray-300 hover:border-blue-600 hover:text-blue-600 transition-all duration-200">
                          Request More Information
                        </Button>
                      </Link>
                      <p className="text-sm text-center text-gray-600 mt-4">
                        Contact us for bulk orders or custom configurations
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Why Buy From Us Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Buy From Us?</h2>
            <p className="text-xl text-gray-600">Professional service and support you can trust</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Expert Setup',
                description: 'Professional onsite installation and configuration of your new system.',
                icon: CheckCircle
              },
              {
                title: 'Local Support',
                description: 'Melbourne-based support team ready to help when you need it.',
                icon: Monitor
              },
              {
                title: 'Warranty Included',
                description: 'Full manufacturer warranty plus our ongoing technical support.',
                icon: Tag
              }
            ].map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="text-blue-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Upgrade Your Hardware?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Contact us today to place your order or discuss your business hardware needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 shadow-xl hover:shadow-2xl transition-all duration-200">
                Contact Us Now
              </Button>
            </Link>
            <a href="tel:0401024062">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 transition-all duration-200">
                Call: 0401 024 062
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
